import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuditLogs, useRemoveAuditLog } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import type { AuditLogEntry } from '@/api/types'
import {
  EmptyState,
  ErrorBanner,
  formatDateTime,
  humanise,
  isoDate,
  LoadingBlock,
  PageHeader,
} from '@/components/common'

/** Every action named in the audit trail, in the order the office would scan them. */
const ACTIONS = [
  'PRICE_CHANGE',
  'DISCOUNT_CHANGE',
  'QUOTATION_APPROVAL',
  'QUOTATION_SENT',
  'QUOTATION_ACCEPTED',
  'ORDER_CONFIRMATION',
  'ORDER_CANCELLATION',
  'ORDER_CHANGE_APPLIED',
  'ORDER_EDITED',
  'HALL_OVERRIDE',
  'HALL_BLOCK_CREATED',
  'INVOICE_CREATED',
  'INVOICE_CHANGE',
  'INVOICE_CANCELLED',
  'PAYMENT_RECORDED',
  'PAYMENT_REVERSAL',
  'REFUND_REQUESTED',
  'REFUND_APPROVED',
  'PERMISSION_CHANGE',
  'USER_CREATED',
  'USER_DISABLED',
  'CUSTOMER_DATA_EXPORT',
  'CUSTOMER_MERGED',
  'ORDER_CUSTOMER_CHANGED',
  'CONFIGURATION_CHANGE',
  'MANPOWER_VARIANCE_APPROVED',
  'RECOVERY_WAIVED',
  'DOCUMENT_PRINTED',
  'DATABASE_BACKUP_DOWNLOADED',
]

/**
 * Every auditable action, across every module, in one list.
 *
 * <p>Append-only to everyone - except an administrator, who can permanently
 * remove an entry. That removal is itself recorded, with what was removed
 * kept against it, so the trail still shows that something was trimmed, by
 * whom and why, even once the entry itself is gone.</p>
 */
export function AuditLogPage() {
  const hasRole = useAuthStore((state) => state.hasRole)
  const isAdmin = hasRole('SUPER_ADMIN') || hasRole('MANAGEMENT')
  const [action, setAction] = useState('')
  const [from, setFrom] = useState(() => isoDate(-30))
  const [to, setTo] = useState(() => isoDate(0))
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)
  const [removing, setRemoving] = useState<AuditLogEntry | null>(null)
  // Opened from a user's own row in Users, so the trail reads as theirs
  // alone rather than the whole business's until this is cleared.
  const [searchParams, setSearchParams] = useSearchParams()
  const performedBy = searchParams.get('performedBy') ?? undefined
  const performedByName = searchParams.get('name') ?? undefined

  const { data, isLoading, error } = useAuditLogs({
    action: action || undefined,
    performedBy,
    from,
    to,
    page,
    size,
  })

  return (
    <>
      <PageHeader
        title="Transaction log"
        subtitle={
          performedBy
            ? `Every auditable action by ${performedByName ?? 'this person'}.`
            : "Every auditable action in the business, who did it and when."
        }
      />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
            <TextField
              select
              label="Action"
              value={action}
              onChange={(event) => {
                setAction(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All actions</MenuItem>
              {ACTIONS.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="From"
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value)
                setPage(0)
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value)
                setPage(0)
              }}
              InputLabelProps={{ shrink: true }}
            />
            {performedBy ? (
              <Chip
                label={`By ${performedByName ?? 'this person'}`}
                onDelete={() => {
                  setSearchParams({})
                  setPage(0)
                }}
              />
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <ErrorBanner error={error} />

      <Card>
        {isLoading ? (
          <CardContent>
            <LoadingBlock />
          </CardContent>
        ) : data && data.content.length === 0 ? (
          <EmptyState message="No actions logged in this window" />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Record</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>IP address</TableCell>
                  {isAdmin ? <TableCell align="right" /> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.content.map((entry) => (
                  <TableRow key={entry.id} hover>
                    <TableCell>{formatDateTime(entry.occurredAt)}</TableCell>
                    <TableCell>{humanise(entry.action)}</TableCell>
                    <TableCell>
                      {entry.entityLabel ?? humanise(entry.entityType) ?? '—'}
                      {entry.entityType ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {humanise(entry.entityType)}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{entry.performedByName ?? '—'}</TableCell>
                    <TableCell>{entry.reason ?? '—'}</TableCell>
                    <TableCell>{entry.ipAddress ?? '—'}</TableCell>
                    {isAdmin ? (
                      <TableCell align="right">
                        <Tooltip title="Permanently remove this entry">
                          <IconButton
                            size="small"
                            aria-label="Remove this entry"
                            onClick={() => setRemoving(entry)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data && (
              <TablePagination
                component="div"
                count={data.totalElements}
                page={data.page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={size}
                onRowsPerPageChange={(event) => {
                  setSize(Number(event.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[25, 50, 100]}
              />
            )}
          </>
        )}
      </Card>

      <RemoveDialog entry={removing} onClose={() => setRemoving(null)} />
    </>
  )
}

function RemoveDialog({ entry, onClose }: { entry: AuditLogEntry | null; onClose: () => void }) {
  const remove = useRemoveAuditLog()
  const [reason, setReason] = useState('')

  const close = () => {
    setReason('')
    onClose()
  }

  return (
    <Dialog open={Boolean(entry)} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Remove this entry</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={remove.error} />
        <Alert severity="warning" sx={{ mb: 2 }}>
          This permanently deletes the entry - it cannot be recovered. The removal itself is written to the
          trail against your name, with what was removed kept beside it.
        </Alert>
        {entry ? (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {humanise(entry.action)} on {entry.entityLabel ?? humanise(entry.entityType)}, by{' '}
            {entry.performedByName ?? 'unknown'} on {formatDateTime(entry.occurredAt)}.
          </Typography>
        ) : null}
        <TextField
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Keep it</Button>
        <Button
          color="error"
          variant="contained"
          disabled={remove.isPending || !entry}
          onClick={async () => {
            if (!entry) return
            await remove.mutateAsync({ id: entry.id, reason: reason.trim() || undefined })
            close()
          }}
        >
          Remove permanently
        </Button>
      </DialogActions>
    </Dialog>
  )
}
